import { useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import {
    Droplets, Sparkles, ShieldCheck, Server, Wifi, Lock, Cloud, GraduationCap,
    Smartphone, Globe, Cog, Database, ArrowRight,
} from "lucide-react";

const DETERGENTS = [
    { icon: Droplets, title: "Liquid Soap", text: "Multi-purpose, biodegradable liquid soap in 500ml, 1L, 5L." },
    { icon: Sparkles, title: "Hand Wash & Sanitiser", text: "Antibacterial, dermatologically tested hand wash range." },
    { icon: ShieldCheck, title: "Laundry & Cleaning", text: "Powder, liquid and bar detergents engineered for tough stains." },
];

const ICT = [
    { icon: Server, title: "IT Support", text: "Reactive and proactive support contracts for SMBs and corporates." },
    { icon: Wifi, title: "Network Installation", text: "Structured cabling, switching, wireless infrastructure." },
    { icon: Lock, title: "Cyber Security", text: "Vulnerability assessments, penetration testing, security audits." },
    { icon: Cloud, title: "Cloud Services", text: "AWS, Azure, Google Cloud migration and managed services." },
    { icon: GraduationCap, title: "ICT Training", text: "Workforce upskilling – office, networking, security tracks." },
    { icon: Cog, title: "Systems Administration", text: "Linux, Windows server management and DevOps." },
];

const SOFTWARE = [
    { icon: Smartphone, title: "Mobile Apps", text: "Flutter and React Native for Android & iOS." },
    { icon: Globe, title: "Web Applications", text: "Modern stacks – React, FastAPI, Node, Postgres, Mongo." },
    { icon: Cog, title: "Business Automation", text: "Workflow, approval, reporting systems built bespoke." },
    { icon: Database, title: "Enterprise Solutions", text: "ERP customisations, integrations, data platforms." },
];

function Section({ id, eyebrow, title, items, image, reverse, accent }) {
    return (
        <section id={id} className="max-w-[1400px] mx-auto px-4 md:px-8 py-24 md:py-32" data-testid={`services-section-${id}`}>
            <div className={`grid md:grid-cols-2 gap-12 items-start ${reverse ? "md:[&>*:first-child]:order-2" : ""}`}>
                <div className="aspect-[4/5] overflow-hidden bg-surface">
                    <img src={image} alt={title} className="w-full h-full object-cover" />
                </div>
                <div>
                    <div className={`overline mb-4 ${accent}`}>{eyebrow}</div>
                    <h2 className="font-display text-4xl md:text-5xl font-bold leading-tight mb-6">{title}</h2>
                    <div className="space-y-6">
                        {items.map((it) => (
                            <div key={it.title} className="flex gap-4">
                                <div className="w-10 h-10 bg-surface border border-border flex items-center justify-center shrink-0">
                                    <it.icon className="w-5 h-5 text-primary" />
                                </div>
                                <div>
                                    <h3 className="font-display font-bold text-lg mb-1">{it.title}</h3>
                                    <p className="text-sm text-muted-foreground leading-relaxed">{it.text}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                    <Link to="/contact" className="mt-8 inline-flex items-center gap-2 font-semibold text-primary hover:gap-3 transition-all">
                        Get a quote <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            </div>
        </section>
    );
}

export default function Services() {
    const loc = useLocation();
    useEffect(() => {
        if (loc.hash) {
            const el = document.getElementById(loc.hash.slice(1));
            if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 100);
        }
    }, [loc.hash]);

    return (
        <div data-testid="services-page">
            <section className="bg-ink text-white py-24 md:py-28">
                <div className="max-w-[1400px] mx-auto px-4 md:px-8">
                    <div className="overline text-primary mb-4">Our services</div>
                    <h1 className="font-display text-5xl md:text-7xl font-bold leading-[1] tracking-tight max-w-4xl">
                        Three businesses.<br/>One promise of excellence.
                    </h1>
                </div>
            </section>

            {/* Tabs */}
            <div className="sticky top-16 md:top-20 z-30 bg-white border-b border-border">
                <div className="max-w-[1400px] mx-auto px-4 md:px-8 flex gap-1 overflow-x-auto no-scrollbar">
                    {[
                        { id: "detergents", label: "Home Detergents" },
                        { id: "ict", label: "ICT Consultancy" },
                        { id: "software", label: "Software Development" },
                    ].map((t) => (
                        <a
                            key={t.id}
                            href={`#${t.id}`}
                            className="px-4 py-4 text-sm font-semibold border-b-2 border-transparent hover:border-primary hover:text-primary transition-colors whitespace-nowrap"
                            data-testid={`services-tab-${t.id}`}
                        >
                            {t.label}
                        </a>
                    ))}
                </div>
            </div>

            <Section
                id="detergents"
                eyebrow="01 — Home Detergents"
                title="Cleaning products you can trust."
                items={DETERGENTS}
                image="https://images.unsplash.com/photo-1583947582886-f40ec6dd5b27?auto=format&fit=crop&w=1200&q=80"
                accent="text-primary"
            />
            <div className="bg-surface">
                <Section
                    id="ict"
                    eyebrow="02 — ICT Consultancy"
                    title="Enterprise-grade ICT services."
                    items={ICT}
                    image="https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=1200&q=80"
                    reverse
                    accent="text-secondary"
                />
            </div>
            <Section
                id="software"
                eyebrow="03 — Software Development"
                title="Software that moves your business."
                items={SOFTWARE}
                image="https://images.unsplash.com/photo-1542831371-29b0f74f9713?auto=format&fit=crop&w=1200&q=80"
                accent="text-accent"
            />
        </div>
    );
}
